import React, { useState, useRef, useEffect, useCallback } from 'react'
import { DownOutlined, CloseOutlined } from '@ant-design/icons'

/**
 * Employee multi-select with search, checkboxes and tags.
 * Mirrors the roster-frontend EmployeeMultiSelect style.
 *
 * options shape: [{ value, label }]
 * value: array of selected `value` entries
 */
const EmployeeMultiSelect = ({
    value = [],
    onChange,
    options = [],
    placeholder = 'All employees',
    disabled = false,
    style = {},
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const containerRef = useRef(null)
    const mountedRef = useRef(true)
    const inputRef = useRef(null)

    useEffect(() => {
        mountedRef.current = true
        return () => {
            mountedRef.current = false
            setIsOpen(false)
            setSearchTerm('')
        }
    }, [])

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                if (mountedRef.current) {
                    setIsOpen(false)
                    setSearchTerm('')
                }
            }
        }
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside)
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [isOpen])

    // Auto-focus search when opened
    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus()
        }
    }, [isOpen])

    const safeSetIsOpen = useCallback((val) => {
        if (mountedRef.current) setIsOpen(val)
    }, [])

    const safeSetSearchTerm = useCallback((val) => {
        if (mountedRef.current) setSearchTerm(val)
    }, [])

    const safeOnChange = useCallback((newValue) => {
        if (mountedRef.current && onChange) onChange(newValue)
    }, [onChange])

    const handleToggleOption = (optValue, event) => {
        event.stopPropagation()
        if (disabled) return
        const newValue = value.includes(optValue)
            ? value.filter((v) => v !== optValue)
            : [...value, optValue]
        safeOnChange(newValue)
    }

    const handleRemoveTag = (optValue, event) => {
        event.stopPropagation()
        if (disabled) return
        safeOnChange(value.filter((v) => v !== optValue))
    }

    const handleClearAll = (event) => {
        event.stopPropagation()
        if (disabled) return
        safeOnChange([])
    }

    const filteredOptions = options.filter((opt) =>
        (opt.label || '').toLowerCase().includes(searchTerm.toLowerCase())
    )

    const selectedOptions = options.filter((opt) => value.includes(opt.value))

    const styles = {
        container: {
            position: 'relative',
            display: 'inline-block',
            minWidth: 200,
            maxWidth: 480,
            zIndex: 1,
            ...style,
        },
        trigger: {
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 4,
            minHeight: 32,
            padding: '4px 8px',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: '#D1D5DB',
            borderRadius: 6,
            backgroundColor: '#fff',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
            transition: 'border-color 0.15s',
            boxSizing: 'border-box',
        },
        triggerOpen: {
            borderColor: '#2563EB',
            boxShadow: '0 0 0 2px rgba(37, 99, 235, 0.1)',
        },
        tag: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '2px 6px',
            backgroundColor: '#EFF6FF',
            color: '#2563EB',
            borderRadius: 4,
            fontSize: 12,
            fontWeight: 500,
        },
        tagRemove: {
            display: 'inline-flex',
            alignItems: 'center',
            cursor: 'pointer',
            color: '#3B82F6',
            fontSize: 10,
        },
        placeholder: {
            color: '#9CA3AF',
            fontSize: 13,
        },
        dropdown: {
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 4,
            backgroundColor: '#fff',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: '#D1D5DB',
            borderRadius: 6,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            zIndex: 2000,
            maxHeight: 280,
            overflow: 'auto',
        },
        searchInput: {
            width: '100%',
            padding: '8px 10px',
            borderTop: 0,
            borderRight: 0,
            borderBottom: '1px solid #E5E7EB',
            borderLeft: 0,
            fontSize: 13,
            outline: 'none',
            boxSizing: 'border-box',
        },
        option: {
            padding: '8px 12px',
            cursor: 'pointer',
            fontSize: 13,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
        },
        checkbox: {
            width: 14,
            height: 14,
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: '#D1D5DB',
            borderRadius: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 10,
            color: '#fff',
            flexShrink: 0,
        },
        checkboxChecked: {
            backgroundColor: '#2563EB',
            borderColor: '#2563EB',
        },
        clearAll: {
            marginLeft: 'auto',
            padding: '2px 6px',
            fontSize: 11,
            color: '#6B7280',
            cursor: 'pointer',
            background: 'transparent',
            borderWidth: 0,
            borderStyle: 'none',
        },
    }

    return (
        <div ref={containerRef} style={styles.container}>
            {/* Trigger */}
            <div
                style={{ ...styles.trigger, ...(isOpen ? styles.triggerOpen : {}) }}
                onClick={() => !disabled && safeSetIsOpen(!isOpen)}
            >
                {selectedOptions.length === 0 ? (
                    <span style={styles.placeholder}>{placeholder}</span>
                ) : (
                    <>
                        {selectedOptions.slice(0, 3).map((opt) => (
                            <span key={opt.value} style={styles.tag}>
                                {opt.label}
                                <CloseOutlined
                                    style={styles.tagRemove}
                                    onClick={(e) => handleRemoveTag(opt.value, e)}
                                />
                            </span>
                        ))}
                        {selectedOptions.length > 3 && (
                            <span style={{ ...styles.tag, backgroundColor: '#F3F4F6', color: '#6B7280' }}>
                                +{selectedOptions.length - 3} more
                            </span>
                        )}
                        <button style={styles.clearAll} onClick={handleClearAll} title="Clear all">
                            Clear all
                        </button>
                    </>
                )}
                <DownOutlined
                    style={{
                        fontSize: 10,
                        color: '#6B7280',
                        marginLeft: 'auto',
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                    }}
                />
            </div>

            {/* Dropdown */}
            {isOpen && (
                <div style={styles.dropdown}>
                    <input
                        ref={inputRef}
                        type="text"
                        style={styles.searchInput}
                        placeholder="Search employees..."
                        value={searchTerm}
                        onChange={(e) => safeSetSearchTerm(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                    />

                    {filteredOptions.length === 0 ? (
                        <div style={{ padding: 12, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>
                            No employees found
                        </div>
                    ) : (
                        filteredOptions.map((opt) => {
                            const isSelected = value.includes(opt.value)
                            return (
                                <div
                                    key={opt.value}
                                    style={{
                                        ...styles.option,
                                        backgroundColor: isSelected ? '#EFF6FF' : '#fff',
                                        color: isSelected ? '#2563EB' : '#111827',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = isSelected ? '#EFF6FF' : '#F3F4F6'
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = isSelected ? '#EFF6FF' : '#fff'
                                    }}
                                    onClick={(e) => handleToggleOption(opt.value, e)}
                                >
                                    <div style={{ ...styles.checkbox, ...(isSelected ? styles.checkboxChecked : {}) }}>
                                        {isSelected && '✓'}
                                    </div>
                                    <span>{opt.label}</span>
                                </div>
                            )
                        })
                    )}
                </div>
            )}
        </div>
    )
}

export default EmployeeMultiSelect
